/**
 * Small DOM-only startup surface used before the React application chunk has
 * loaded. Keeping this outside React means it can render synchronously from the
 * browser entry, so a cold dev start never presents an empty root.
 */
export type StartupState = "loading" | "error";

const STARTUP_STYLE =
  "box-sizing:border-box;min-height:100dvh;display:grid;place-items:center;padding:2rem;background:#0b1f3a;color:#fff;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";
const STARTUP_PANEL_STYLE =
  "box-sizing:border-box;width:min(100%,28rem);padding:2rem;border:1px solid rgba(255,255,255,.18);border-radius:1rem;background:rgba(255,255,255,.08);text-align:center";
const STARTUP_SPINNER_STYLE =
  "display:inline-block;width:2rem;height:2rem;border:3px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:9999px;animation:aero-startup-spin 1s linear infinite";

function text(
  tagName: keyof HTMLElementTagNameMap,
  value: string,
): HTMLElement {
  const element = document.createElement(tagName);
  element.textContent = value;
  return element;
}

/**
 * Replace the root with an accessible, dependency-free startup state.
 *
 * The returned button is only present for the error state. Callers own the
 * retry behavior so this helper stays independent from the boot decision
 * (including the anonymous/authenticated gate decision).
 */
export function renderStartupState(
  container: HTMLElement,
  state: StartupState,
): HTMLButtonElement | null {
  container.replaceChildren();
  container.setAttribute("aria-busy", state === "loading" ? "true" : "false");
  container.dataset.aeroStartupState = state;

  const main = document.createElement("main");
  main.style.cssText = STARTUP_STYLE;
  main.setAttribute(
    "aria-labelledby",
    state === "loading"
      ? "aero-startup-loading-title"
      : "aero-startup-error-title",
  );

  const panel = document.createElement("div");
  panel.style.cssText = STARTUP_PANEL_STYLE;

  if (state === "loading") {
    const spinner = document.createElement("span");
    spinner.style.cssText = STARTUP_SPINNER_STYLE;
    spinner.setAttribute("aria-hidden", "true");

    const heading = text("h1", "Loading AERO…");
    heading.id = "aero-startup-loading-title";
    heading.style.cssText = "margin:1rem 0 0;font-size:1.125rem;font-weight:600";

    const status = text("p", "Preparing your aviation workspace.");
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    status.style.cssText = "margin:.5rem 0 0;color:rgba(255,255,255,.72)";

    panel.append(spinner, heading, status);
    main.append(panel);
    container.append(main);

    // The keyframes are scoped by a unique name and removed with the startup
    // tree, so this does not add a permanent stylesheet or affect app content.
    const style = document.createElement("style");
    style.textContent =
      "@keyframes aero-startup-spin{to{transform:rotate(360deg)}}";
    panel.append(style);
    return null;
  }

  const heading = text("h1", "AERO is still loading");
  heading.id = "aero-startup-error-title";
  heading.style.cssText = "margin:0;font-size:1.25rem;font-weight:700";

  const message = text(
    "p",
    "We couldn't finish loading the app. Check your connection and try again.",
  );
  message.style.cssText = "margin:1rem 0 0;color:rgba(255,255,255,.72)";

  const retry = text("button", "Try again") as HTMLButtonElement;
  retry.type = "button";
  retry.style.cssText =
    "margin-top:1.5rem;min-height:2.75rem;padding:.65rem 1.25rem;border:0;border-radius:9999px;background:#fff;color:#0b1f3a;font:inherit;font-weight:600;cursor:pointer";

  panel.setAttribute("role", "alert");
  panel.append(heading, message, retry);
  main.append(panel);
  container.append(main);
  return retry;
}

/** Remove startup-only markers before React takes ownership of the root. */
export function clearStartupState(container: HTMLElement): void {
  container.removeAttribute("aria-busy");
  delete container.dataset.aeroStartupState;
}